import axiosStatic, { AxiosError, AxiosInstance, AxiosResponse } from 'axios'
import { Store } from 'redux'

import { ConfigActions } from '../redux/config'
import { LogActions } from '../redux/logs'
import { UserActions } from '../redux/user'
import { getToken, isUserLoggedIn } from '../redux/user/selectors'

import { autoLogout } from './autoLogout'
import { mapActionError } from './helpers'

const REFRESH_URL = '/api/token/get'
const XSRF_REFRESH_URL = '/api/xsrf'

async function refreshAccessToken(axios: AxiosInstance) {
  const { data }: AxiosResponse<{ isContractActive: boolean; isManualAccounting: boolean; token: string }> =
    await axios({
      url: REFRESH_URL,
      method: 'get',
      withCredentials: true
    })

  autoLogout.scheduleAutoLogout()
  return data
}

async function refreshXsrfToken(axios: AxiosInstance) {
  await axios({ url: XSRF_REFRESH_URL })
}

export function formatAuthHeader(token: string) {
  return `Bearer ${token}`
}

export function applyAxiosInterceptors(axios: AxiosInstance, store: Store) {
  autoLogout.setStore(store)

  axios.interceptors.request.use(
    async config => {
      const token = getToken(store.getState())
      if (token) {
        config.headers.Authorization = formatAuthHeader(token)
      }
      return config
    },
    error => Promise.reject(error)
  )

  axios.interceptors.response.use(undefined, async (error: AxiosError<any>) => {
    const axiosError = error
    if (axiosStatic.isCancel(error)) return Promise.reject(error)
    if (axiosError.response) {
      switch (axiosError.response.status) {
        case 401: {
          const originalRequest = axiosError.config
          // @ts-ignore
          if (originalRequest.url === REFRESH_URL && !originalRequest._retry) {
            return Promise.resolve({ data: { Token: '' } })
          }

          const loggedIn = isUserLoggedIn(store.getState())
          if (!loggedIn) return Promise.reject(error)

          // @ts-ignore
          if (originalRequest && !originalRequest._retry) {
            //@ts-ignore
            originalRequest._retry = true
            try {
              const data = await refreshAccessToken(axios)
              if (data.isManualAccounting || data.isContractActive) {
                store.dispatch(UserActions.setToken({ token: data.token }))
                originalRequest.headers.Authorization = formatAuthHeader(data.token)

                await refreshXsrfToken(axios)
                return axios(originalRequest)
              } else {
                store.dispatch(UserActions.detectInvalidContract())
                break
              }
            } catch (e: any) {
              store.dispatch(LogActions.addLogItem(mapActionError(e, 'ERROR', window.location.href)))
            }
          }
          store.dispatch(UserActions.logout({ isLoginAsMode: false }))
          break
        }
        case 429: {
          if (axiosError.response.data !== 'Password reset blocked.') {
            store.dispatch(ConfigActions.setApiBlocked(true))
            break
          }
        }
      }
    }
    return Promise.reject(error)
  })
}
